/*
* SessionManager.ts
* @author Abhilash Panwar (abpanwar) Haorui Guo (haoruiguo)
* @copyright Microsoft 2020
* File containing apis to help send SDK usage stats.
*/
import { EventsDiscardedReason } from '@microsoft/1ds-core-js';
import { _StatsEventName, _eventSent, _eventDiscardedInvalidEvent, _eventDiscardedKillSwitch, _eventDiscardedNonRetryableStatus, _eventDiscardedQueueFull, _eventDiscardedSizeLimitExceeded, _eventDiscardedUnknown, _eventDiscarded } from './Constants';
var StatsTimer = 60000;
var StatsManager = /** @class */ (function () {
    /**
     * Creates an instance of the stats manager class to send SDK usage statistics.
     * @param sendStats stats will be sent to the tenant using this function.
     */
    function StatsManager(sendStats) {
        var _this = this;
        this._isInitalized = false;
        this._stats = {};
        this._isInitalized = true;
        this._sendStats = sendStats;
        setTimeout(function () { return _this._flush(); }, StatsTimer);
    }
    /**
     * Creates an event if there's stats and calls sendStats.
     */
    StatsManager.prototype._flush = function () {
        var _this = this;
        if (this._isInitalized) {
            for (var ingestionKey in this._stats) {
                if (this._stats.hasOwnProperty(ingestionKey)) {
                    this._sendStats(this._stats[ingestionKey], ingestionKey);
                }
            }
            this._stats = {};
            this._timeoutId = setTimeout(function () { return _this._flush(); }, StatsTimer);
        }
    };
    /**
     * Flush the current stats and stop the stats collection.
     */
    StatsManager.prototype._teardown = function () {
        if (this._isInitalized) {
            this._flush();
            clearTimeout(this._timeoutId);
            this._isInitalized = false;
        }
    };
    /**
     * Add stats for events successfully sent from SDK.
     */
    StatsManager.prototype._addStatForEventSent = function (events) {
        events = this._getNonStatsEvents(events);
        if (events.length === 0)
            return;
        this._addStatForEvents(events, _eventSent);
    };
    /**
     * Add stats for events being requested to send but were discarded by SDK
     */
    StatsManager.prototype._addStatForEventDiscarded = function (events, reason) {
        events = this._getNonStatsEvents(events);
        if (events.length === 0)
            return;
        this._addStatForEvents(events, _eventDiscarded);
        switch (reason) {
            case EventsDiscardedReason.InvalidEvent:
                this._addStatForEvents(events, _eventDiscardedInvalidEvent);
                break;
            case EventsDiscardedReason.KillSwitch:
                this._addStatForEvents(events, _eventDiscardedKillSwitch);
                break;
            case EventsDiscardedReason.NonRetryableStatus:
                this._addStatForEvents(events, _eventDiscardedNonRetryableStatus);
                break;
            case EventsDiscardedReason.QueueFull:
                this._addStatForEvents(events, _eventDiscardedQueueFull);
                break;
            case EventsDiscardedReason.SizeLimitExceeded:
                this._addStatForEvents(events, _eventDiscardedSizeLimitExceeded);
                break;
            case EventsDiscardedReason.Unknown:
                this._addStatForEvents(events, _eventDiscardedUnknown);
                break;
        }
    };
    StatsManager.prototype._addStatForEvents = function (events, stat) {
        for (var _i = 0, events_1 = events; _i < events_1.length; _i++) {
            var event_1 = events_1[_i];
            this._addStat(stat, 1, event_1.iKey);
        }
    };
    StatsManager.prototype._addStat = function (statName, value, ingestionKey) {
        if (this._isInitalized) {
            if (!this._stats[ingestionKey]) {
                this._stats[ingestionKey] = {};
            }
            if (!this._stats[ingestionKey][statName]) {
                this._stats[ingestionKey][statName] = value;
            }
            else {
                this._stats[ingestionKey][statName] = this._stats[ingestionKey][statName] + value;
            }
        }
    };
    StatsManager.prototype._getNonStatsEvents = function (events) {
        var nonStatsEvents = [];
        for (var _i = 0, events_2 = events; _i < events_2.length; _i++) {
            var event_2 = events_2[_i];
            if (event_2.name !== _StatsEventName) {
                nonStatsEvents.push(event_2);
            }
        }
        return nonStatsEvents;
    };
    return StatsManager;
}());
export default StatsManager;
//# sourceMappingURL=StatsManager.js.map